import { useEffect, useRef, useState } from 'react';

type Props = {
  valueIso: string | null;
  disabled?: boolean;
  onSubmit: (nextIso: string | null) => Promise<void>;
  ariaLabel?: string;
};

function isoToLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Date cell used on the rollout master grid. Wraps a native datetime-local
// input and shows a brief "✓ Updated" flash after a successful commit so the
// AM is sure their pick registered. Flash duration ~1.8s.
export function DatePickerCell({ valueIso, disabled, onSubmit, ariaLabel }: Props) {
  const [flashing, setFlashing] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!flashing) return;
    const handle = window.setTimeout(() => setFlashing(false), 1800);
    return () => window.clearTimeout(handle);
  }, [flashing]);

  const commit = async (local: string) => {
    if (disabled) return;
    if (submittingRef.current) return;
    const iso = localToIso(local);
    if (iso === valueIso) return;
    submittingRef.current = true;
    try {
      await onSubmit(iso);
      setFlashing(true);
    } catch {
      // Swallow -- MasterGrid's mutation error path surfaces the failure via
      // existing toast/error UX. We just suppress the flash in that case.
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <span className="relative inline-block">
      <input
        type="datetime-local"
        defaultValue={isoToLocal(valueIso)}
        disabled={disabled}
        onBlur={(e) => void commit(e.target.value)}
        aria-label={ariaLabel}
        className={`rounded border px-1 py-0.5 text-xs transition-colors duration-500 ${
          flashing ? 'bg-emerald-100 border-emerald-400' : 'bg-white border-slate-200'
        }`}
      />
      {flashing && (
        <span
          className="absolute -top-1 -right-1 text-[10px] bg-emerald-500 text-white px-1 rounded shadow-sm pointer-events-none"
          aria-hidden
        >
          ✓ Updated
        </span>
      )}
    </span>
  );
}
